import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { saveProductCategory } from '../utilities/api';
import './CategoryDialog.css';

const CategoryproductDialog = ({ visible, onHide, categoryData, clients, branches, allCategories, onSaveSuccess, toast }) => {
    const [category, setCategory] = useState({
        category_master_id: null,
        prcat_cat_id: null,
        prcat_cat_name: '',
        client_name: '',
        branch_name: '',
        branch_id: '',
        prcat_tokengroup: 0,
        prcat_status: 'Active',
        prcat_discount: 0,
        effective_from: null
    });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modalFilteredBranches, setModalFilteredBranches] = useState([]);

    useEffect(() => {
        if (categoryData) {
            setCategory({
                category_master_id: categoryData.category_master_id || null,
                prcat_cat_id: categoryData.prcat_cat_id || null,
                prcat_cat_name: categoryData.prcat_cat_name || '',
                client_name: categoryData.client_name || '',
                branch_name: categoryData.branch_name || '',
                branch_id: categoryData.branch_id || '',
                prcat_tokengroup: categoryData.prcat_tokengroup || 0,
                prcat_status: categoryData.prcat_status || 'Active',
                prcat_discount: categoryData.prcat_discount || 0,
                effective_from: categoryData.effective_from ? new Date(categoryData.effective_from) : null
            });
        } else {
            setCategory({
                category_master_id: null,
                prcat_cat_id: null,
                prcat_cat_name: '',
                client_name: '',
                branch_name: '',
                branch_id: '',
                prcat_tokengroup: 0,
                prcat_status: 'Active',
                prcat_discount: 0,
                effective_from: null
            });
        }
        setSubmitted(false);
    }, [categoryData, visible]);

    // Update branch dropdown based on selected client
    useEffect(() => {
        if (visible && category?.client_name && clients?.length > 0) {
            const selectedClientObj = clients.find((c) => c.client_name === category.client_name);
            if (selectedClientObj) {
                const targetId = selectedClientObj.client_id;
                const filtered = branches.filter((b) => String(b.client_id) === String(targetId));
                setModalFilteredBranches(filtered);
            } else {
                setModalFilteredBranches([]);
            }
        } else {
            setModalFilteredBranches([]);
        }
    }, [category?.client_name, branches, clients, visible]);

    const onInputChange = (e, name) => {
        const val = e.target && e.target.value !== undefined ? e.target.value : e.value;
        let _category = { ...category };
        _category[`${name}`] = val;

        if (name === 'client_name') {
            _category['branch_name'] = '';
            _category['branch_id'] = '';
        }

        if (name === 'branch_name') {
            const selectedBranch = modalFilteredBranches.find((b) => b.branch_name === val);
            _category['branch_id'] = selectedBranch?.branch_id || '';
        }

        setCategory(_category);
    };

    const validateForm = () => {
        const errors = [];

        // Required field validations
        if (!category.client_name?.trim()) {
            errors.push('Client is required');
        }

        if (!category.branch_name?.trim()) {
            errors.push('Branch is required');
        }

        if (!category.prcat_cat_name?.trim()) {
            errors.push('Category Name is required');
        } else if (category.prcat_cat_name.trim().length < 2) {
            errors.push('Category Name must be at least 2 characters long');
        } else if (category.prcat_cat_name.trim().length > 100) {
            errors.push('Category Name must not exceed 100 characters');
        }

        // Discount validation
        if (category.prcat_discount < 0 || category.prcat_discount > 100) {
            errors.push('Discount must be between 0 and 100');
        }

        // Token group validation
        if (category.prcat_tokengroup < 0) {
            errors.push('Token Group must be a positive number');
        }

        // Status validation
        if (!['Active', 'Inactive'].includes(category.prcat_status)) {
            errors.push('Invalid status selected');
        }

        // Show all errors
        if (errors.length > 0) {
            toast.current.show({
                severity: 'warn',
                summary: 'Validation Failed',
                detail: errors.join(', '),
                life: 5000
            });
            return false;
        }

        return true;
    };

    const saveCategory = async () => {
        setSubmitted(true);

        if (!validateForm()) {
            return;
        }

        // Duplicate check
        const isDuplicate = allCategories.some(
            (item) =>
                item.client_name === category.client_name &&
                item.branch_name === category.branch_name &&
                (item.category_name || item.prcat_cat_name || '').toLowerCase().trim() === category.prcat_cat_name.toLowerCase().trim() &&
                item.prcat_cat_id !== category.prcat_cat_id
        );

        if (isDuplicate) {
            toast.current.show({
                severity: 'warn',
                summary: 'Duplicate Found',
                detail: 'This category already exists for the selected Client and Branch.',
                life: 4000
            });
            return;
        }

        setLoading(true);
        try {
            const clientObj = clients.find((c) => c.client_name === category.client_name);

            const payload = {
                categories: [
                    {
                        prcat_cat_id: category.prcat_cat_id || null,
                        category_name: category.prcat_cat_name.trim(),
                        client_id: clientObj?.client_id || null,
                        branch_id: category.branch_id,
                        token_group: String(category.prcat_tokengroup || 0),
                        status: category.prcat_status,
                        discount: String(category.prcat_discount || 0),
                        effective_from: category.effective_from instanceof Date ? category.effective_from.toISOString().split('T')[0] : category.effective_from
                    }
                ],
                updated_by: localStorage.getItem('userid')
            };

            const result = await saveProductCategory(payload);
            if (result.status === 'success') {
                toast.current.show({
                    severity: 'success',
                    summary: 'Success',
                    detail: categoryData ? 'Category updated successfully' : 'Category added successfully',
                    life: 3000
                });
                onSaveSuccess();
                onHide();
            } else {
                toast.current.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: result.message || 'Save failed'
                });
            }
        } catch (error) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'API Communication failure'
            });
        } finally {
            setLoading(false);
        }
    };

    const dialogFooter = (
        <div className="dialog-footer">
            <Button label="Cancel" icon="pi pi-times" className="p-button-text p-button-secondary cancel-btn" onClick={onHide} disabled={loading} />
            <Button label={categoryData ? 'Update' : 'Save'} icon="pi pi-check" className="p-button-raised save-btn" onClick={saveCategory} loading={loading} />
        </div>
    );

    return (
        <Dialog
            visible={visible}
            style={{ width: '550px' }}
            header={
                <div className="dialog-header-custom">
                    <i className="pi pi-tag header-icon"></i>
                    <span>{categoryData ? 'Edit Product Category' : 'Add Product Category'}</span>
                </div>
            }
            modal
            className="category-dialog"
            onHide={onHide}
            footer={dialogFooter}
            draggable={false}
            resizable={false}
        >
            <div className="dialog-content">
                <div className="field-group">
                    <label className="field-label required">
                        <i className="pi pi-building label-icon"></i>
                        Client
                    </label>
                    <Dropdown
                        value={category.client_name}
                        options={clients}
                        optionLabel="client_name"
                        optionValue="client_name"
                        onChange={(e) => onInputChange(e, 'client_name')}
                        placeholder="Select Client"
                        filter
                        className={`custom-dropdown ${submitted && !category.client_name ? 'p-invalid' : ''}`}
                        showClear
                    />
                    {submitted && !category.client_name && (
                        <small className="error-message">
                            <i className="pi pi-exclamation-circle"></i>
                            Client is required
                        </small>
                    )}
                </div>

                <div className="field-group">
                    <label className="field-label required">
                        <i className="pi pi-map-marker label-icon"></i>
                        Branch
                    </label>
                    <Dropdown
                        value={category.branch_name}
                        options={modalFilteredBranches}
                        optionLabel="branch_name"
                        optionValue="branch_name"
                        onChange={(e) => onInputChange(e, 'branch_name')}
                        placeholder={category.client_name ? 'Select Branch' : 'Select Client First'}
                        filter
                        disabled={!category.client_name}
                        className={`custom-dropdown ${submitted && !category.branch_name ? 'p-invalid' : ''}`}
                        showClear
                    />
                    {submitted && !category.branch_name && (
                        <small className="error-message">
                            <i className="pi pi-exclamation-circle"></i>
                            Branch is required
                        </small>
                    )}
                </div>

                <div className="field-group">
                    <label className="field-label required">
                        <i className="pi pi-tag label-icon"></i>
                        Category Name
                    </label>
                    <InputText
                        value={category.prcat_cat_name}
                        onChange={(e) => onInputChange(e, 'prcat_cat_name')}
                        className={`custom-input ${submitted && !category.prcat_cat_name ? 'p-invalid' : ''}`}
                        placeholder="Enter category name"
                        maxLength={100}
                    />
                    {submitted && !category.prcat_cat_name && (
                        <small className="error-message">
                            <i className="pi pi-exclamation-circle"></i>
                            Category name is required
                        </small>
                    )}
                </div>

                <div className="field-row">
                    <div className="field-group field-half">
                        <label className="field-label">
                            <i className="pi pi-percentage label-icon"></i>
                            Discount (%)
                        </label>
                        <InputNumber
                            value={Number(category.prcat_discount)}
                            onValueChange={(e) => {
                                let _cat = { ...category };
                                _cat.prcat_discount = e.value || 0;
                                setCategory(_cat);
                            }}
                            min={0}
                            max={100}
                            className="custom-input-number"
                            placeholder="0"
                            showButtons
                            buttonLayout="horizontal"
                            decrementButtonClassName="p-button-secondary"
                            incrementButtonClassName="p-button-secondary"
                            incrementButtonIcon="pi pi-plus"
                            decrementButtonIcon="pi pi-minus"
                        />
                    </div>

                    <div className="field-group field-half">
                        <label className="field-label">
                            <i className="pi pi-users label-icon"></i>
                            Token Group
                        </label>
                        <InputNumber
                            value={Number(category.prcat_tokengroup)}
                            onValueChange={(e) => {
                                let _cat = { ...category };
                                _cat.prcat_tokengroup = e.value || 0;
                                setCategory(_cat);
                            }}
                            min={0}
                            className="custom-input-number"
                            placeholder="0"
                            showButtons
                            buttonLayout="horizontal"
                            decrementButtonClassName="p-button-secondary"
                            incrementButtonClassName="p-button-secondary"
                            incrementButtonIcon="pi pi-plus"
                            decrementButtonIcon="pi pi-minus"
                        />
                    </div>
                </div>

                <div className="field-group">
                    <label className="field-label required">
                        <i className="pi pi-circle label-icon"></i>
                        Status
                    </label>
                    <Dropdown
                        value={category.prcat_status}
                        options={[
                            { label: 'Active', value: 'Active' },
                            { label: 'Inactive', value: 'Inactive' }
                        ]}
                        onChange={(e) => onInputChange(e, 'prcat_status')}
                        className="custom-dropdown"
                        optionLabel="label"
                        optionValue="value"
                    />
                </div>

                <div className="field-group">
                    <label className="field-label">
                        <i className="pi pi-calendar label-icon"></i>
                        Effective From
                    </label>
                    <Calendar value={category.effective_from} onChange={(e) => onInputChange(e, 'effective_from')} dateFormat="yy-mm-dd" showIcon className="custom-calendar" placeholder="Select date" showButtonBar />
                </div>
            </div>
        </Dialog>
    );
};

export default CategoryproductDialog;
